export type ServiceType = 'resume-review' | 'culture-call' | 'referral-session';

/**
 * Scheduling model determines how slots are created and displayed:
 * - async_delivery: No time slots. Job seeker submits, professional delivers within a window.
 * - fixed_duration_call: System auto-generates slots at a fixed duration (e.g., 30 min).
 * - variable_duration_call: Professional picks a duration from allowedDurations.
 */
export type SchedulingModel = 'async_delivery' | 'fixed_duration_call' | 'variable_duration_call';

export interface SlotDay {
  day: string;
  slots: string[];
}

export interface WorkExperienceEntry {
  role: string;
  company: string;
  period: string;
  summary?: string;
}

export interface Professional {
  id: string;
  alias: string;
  name: string;
  title: string;
  rating: number;
  reviews: number;
  /** 0..1 multi-signal marketplace rank (server-computed; Bayesian fallback client-side). */
  rankScore?: number;
  price: number;
  description: string;
  specialties: string[];
  weeklySlots: SlotDay[];
  image?: string;
  company?: string;
  yearsOfExperience?: number;
  verified?: boolean;
  /** Extended profile fields (public view excludes phone & emails) */
  designation?: string;
  bio?: string;
  location?: string;
  country?: string;
  city?: string;
  state?: string;
  preferredLocations?: string;
  industry?: string;
  noticePeriod?: string;
  availabilityStatus?: string;
  portfolioUrl?: string;
  linkedinLink?: string;
  githubLink?: string;
  education?: string;
  certifications?: string;
  languages?: string;
  skills?: string[];
  workExperience?: WorkExperienceEntry[];
  joinDate?: string;
  /** Slot duration in minutes set by the professional for this service */
  slotDurationMinutes?: number;
  /** Minimum price across all active services (populated in Multi-Service Context). */
  startingPrice?: number;
  /** True when the professional has more than one active service configuration. */
  isMultiService?: boolean;
}

export interface ServiceConfig {
  type: ServiceType;
  title: string;
  subtitle: string;
  heroTag: string;
  fixedPriceLabel: string;
  slotRequired: boolean;
  asyncFeedback: boolean;
  schedulingModel: SchedulingModel;
  /** Fixed slot duration in minutes (null for async, set for fixed_duration_call) */
  slotDurationMinutes: number | null;
  /** Allowed durations the professional can pick (for variable_duration_call) */
  allowedDurations?: number[];
  /** Delivery window in hours (for async_delivery services like resume review) */
  deliveryWindowHours?: number;
  /** Buffer time in minutes between auto-generated slots */
  slotBufferMinutes: number;
  fieldLabels: {
    primary?: string;
    secondary?: string;
    tertiary?: string;
  };
  helperPoints: string[];
}

export const SERVICE_CONFIGS: Record<ServiceType, ServiceConfig> = {
  'resume-review': {
    type: 'resume-review',
    title: 'Book Resume Review',
    subtitle: 'Resume Services',
    heroTag: 'Resume upload required',
    fixedPriceLabel: '₹299',
    slotRequired: false,
    asyncFeedback: true,
    schedulingModel: 'async_delivery',
    slotDurationMinutes: null,
    deliveryWindowHours: 48,
    slotBufferMinutes: 0,
    fieldLabels: {
      primary: 'Anything specific to focus on?',
      secondary: 'JD link (optional)',
      tertiary: 'Upload your resume (PDF or DOCX)',
    },
    helperPoints: ['No slot picking needed', 'Feedback delivered within 48 hours', 'Auto-refund if not delivered in time'],
  },
  'culture-call': {
    type: 'culture-call',
    title: 'Book Mock Interview',
    subtitle: 'Mock Interview',
    heroTag: '30-minute video session',
    fixedPriceLabel: '₹500–₹3,000',
    slotRequired: true,
    asyncFeedback: false,
    schedulingModel: 'fixed_duration_call',
    slotDurationMinutes: 30,
    slotBufferMinutes: 5,
    fieldLabels: {
      primary: 'What do you want to learn about this company?',
      tertiary: 'Meeting link will appear after the professional adds it.',
    },
    helperPoints: ['Slot locks once payment is placed', 'Add to Google Calendar after confirmation'],
  },
  'referral-session': {
    type: 'referral-session',
    title: 'Book Referral Session',
    subtitle: 'Referral Connect',
    heroTag: 'Slot locks on payment',
    fixedPriceLabel: '₹500–₹3,000',
    slotRequired: true,
    asyncFeedback: false,
    schedulingModel: 'variable_duration_call',
    slotDurationMinutes: null,
    allowedDurations: [15, 20, 25, 30],
    slotBufferMinutes: 5,
    fieldLabels: {
      primary: 'Which role/JD are you targeting?',
      secondary: 'JD URL',
      tertiary: "What's your biggest concern about your profile?",
    },
    helperPoints: ['Professional chooses the final price', 'Payment covers time, not a guaranteed referral'],
  },
};

const resumeReviewProfessionals: Professional[] = [];

const cultureCallProfessionals: Professional[] = [];

const referralProfessionals: Professional[] = [];

export const PROFESSIONALS_BY_SERVICE: Record<ServiceType, Professional[]> = {
  'resume-review': resumeReviewProfessionals,
  'culture-call': cultureCallProfessionals,
  'referral-session': referralProfessionals,
};
